import {
  createApiClient,
  createAuthStore,
  type AmenityDto,
  type BookingResponse,
  type CreateBookingRequest,
  type CreateOrderResponse,
  type CreateReviewRequest,
  type GstSlabDto,
  type HouseRuleDto,
  type LoginRequest,
  type LoginResponse,
  type SendOtpRequest,
  type VerifyOtpRequest,
  type ModifyBookingRequest,
  type PageResponse,
  type PropertyResponse,
  type PropertyTypeDto,
  type PublicAvailabilityResponse,
  type ReviewResponse,
  type SignupRequest,
  type VerifyPaymentRequest,
  type VerifyPaymentResponse,
} from '@ota/api-client';
import type { PropertySuggestion } from '@ota/design-system';

export const authStore = createAuthStore('public');

export const api = createApiClient({
  baseURL: '/api',
  appId: 'public',
  // Note: no onUnauthenticated callback any more. The 401-handling is now
  // centralised — the api-client interceptor fires sessionExpiredStore, and
  // the <SessionExpiredDialog/> mounted at the app root decides whether to
  // show a dialog (only when the user had an active session) and handles
  // the redirect + returnTo.
});

// ----- auth -----
export async function login(req: LoginRequest): Promise<LoginResponse> {
  const { data } = await api.post<LoginResponse>('/auth/login', req);
  authStore.set({
    expiresAt: Date.now() + data.expiresInSeconds * 1000,
    user: data.user,
  });
  return data;
}

export async function signup(req: SignupRequest): Promise<LoginResponse> {
  const { data } = await api.post<LoginResponse>('/auth/signup', req);
  authStore.set({
    expiresAt: Date.now() + data.expiresInSeconds * 1000,
    user: data.user,
  });
  return data;
}

export async function logout() {
  try {
    // Best-effort: invalidate the Redis session on the backend.
    // We don't await the result — even if it fails, clearing the local
    // token means the user is effectively logged out on this device.
    await api.post('/auth/logout');
  } catch {
    // ignore — token may already be expired
  } finally {
    authStore.clear();
  }
}

// ----- public properties -----
export interface SearchParams {
  city?: string;
  area?: string;
  /** Hero search bar's combined "city, area or property" free-text box — OR-matched
   *  across name/area/city/landmark on the backend. Independent of city/area above,
   *  which the listing sidebar's structured filters still use. */
  q?: string;
  /** Set instead of q when a specific hotel was picked from the suggestion dropdown —
   *  takes precedence over every other filter on the backend. */
  propertyId?: number;
  minPrice?: number;
  maxPrice?: number;
  minRating?: number;
  starRatings?: number[];
  propertyTypeCodes?: string[];
  amenityCodes?: string[];
  houseRuleCodes?: string[];
  sort?: 'popularity' | 'priceAsc' | 'priceDesc' | 'ratingDesc' | 'bestValue';
  page?: number;
  size?: number;
  /** ISO date (YYYY-MM-DD). When both checkIn & checkOut are sent, the
   *  response carries roomsAvailable per property and the card renders Sold Out. */
  checkIn?: string;
  checkOut?: string;
}

export async function searchProperties(params: SearchParams): Promise<PageResponse<PropertyResponse>> {
  // Axios serialises arrays as repeated keys when paramsSerializer is set this way,
  // which Spring binds to Set<T> @RequestParams cleanly.
  const { data } = await api.get<PageResponse<PropertyResponse>>('/properties/public/search', {
    params,
    paramsSerializer: {
      indexes: null, // ?starRatings=4&starRatings=5 instead of starRatings[]=
    },
  });
  return data;
}

/** Live suggestions for the search bar's autocomplete dropdown. */
export async function fetchPropertySuggestions(q: string): Promise<PropertySuggestion[]> {
  const { data } = await api.get<PropertySuggestion[]>('/properties/public/suggest', {
    params: { q },
  });
  return data;
}

export async function fetchProperty(id: number): Promise<PropertyResponse> {
  const { data } = await api.get<PropertyResponse>(`/properties/${id}`);
  return data;
}

export async function fetchPropertyTypes(): Promise<PropertyTypeDto[]> {
  const { data } = await api.get<PropertyTypeDto[]>('/property-types');
  return data;
}

export async function fetchAmenities(): Promise<AmenityDto[]> {
  const { data } = await api.get<AmenityDto[]>('/amenities');
  return data;
}

export async function fetchHouseRules(): Promise<HouseRuleDto[]> {
  const { data } = await api.get<HouseRuleDto[]>('/house-rules');
  return data;
}

export async function fetchAvailability(
  propertyId: number,
  checkIn: string,
  checkOut: string,
): Promise<PublicAvailabilityResponse> {
  const { data } = await api.get<PublicAvailabilityResponse>(
    `/public/properties/${propertyId}/availability`,
    { params: { checkIn, checkOut } },
  );
  return data;
}

// ----- bookings -----
export async function createBooking(req: CreateBookingRequest): Promise<BookingResponse> {
  const { data } = await api.post<BookingResponse>('/bookings', req);
  return data;
}

export async function fetchMyBookings(page = 0, size = 20): Promise<PageResponse<BookingResponse>> {
  const { data } = await api.get<PageResponse<BookingResponse>>('/bookings/mine', {
    params: { page, size },
  });
  return data;
}

export async function cancelBooking(id: number): Promise<BookingResponse> {
  const { data } = await api.post<BookingResponse>(`/bookings/${id}/cancel`);
  return data;
}

export async function fetchRefundPreview(id: number): Promise<import('@ota/api-client').RefundQuote> {
  const { data } = await api.get<import('@ota/api-client').RefundQuote>(`/bookings/${id}/refund-preview`);
  return data;
}

export async function fetchCancellationPolicy(id: number): Promise<import('@ota/api-client').CancellationPolicyDto> {
  const { data } = await api.get<import('@ota/api-client').CancellationPolicyDto>(`/cancellation-policies/${id}`);
  return data;
}

export async function modifyBooking(id: number, req: ModifyBookingRequest): Promise<BookingResponse> {
  const { data } = await api.patch<BookingResponse>(`/bookings/${id}`, req);
  return data;
}

/** Preview a proposed modification's price impact before submitting it. */
export async function fetchModifyPreview(
  id: number, req: ModifyBookingRequest,
): Promise<import('@ota/api-client').PriceChangeQuote> {
  const { data } = await api.get<import('@ota/api-client').PriceChangeQuote>(
    `/bookings/${id}/modify-preview`, { params: req },
  );
  return data;
}

export async function fetchBooking(id: number): Promise<BookingResponse> {
  const { data } = await api.get<BookingResponse>(`/bookings/${id}`);
  return data;
}

// ----- public (token-based, no session) booking access -----
// Mirrors fetchMyBookings/fetchBooking/cancelBooking/modifyBooking/fetchRefundPreview
// above, but authorized by a signed link token instead of the session cookie —
// used by MyBookingsPage/BookingDetailPage when opened from the "View my
// bookings" link in the confirmation email rather than while logged in. See
// PublicBookingController on the backend.

export async function fetchMyBookingsPublic(
  token: string, page = 0, size = 20,
): Promise<PageResponse<BookingResponse>> {
  const { data } = await api.get<PageResponse<BookingResponse>>('/public/bookings/mine', {
    params: { token, page, size },
  });
  return data;
}

export async function fetchBookingPublic(id: number, token: string): Promise<BookingResponse> {
  const { data } = await api.get<BookingResponse>(`/public/bookings/${id}`, { params: { token } });
  return data;
}

export async function cancelBookingPublic(id: number, token: string): Promise<BookingResponse> {
  const { data } = await api.post<BookingResponse>(`/public/bookings/${id}/cancel`, undefined, { params: { token } });
  return data;
}

export async function modifyBookingPublic(
  id: number, req: ModifyBookingRequest, token: string,
): Promise<BookingResponse> {
  const { data } = await api.patch<BookingResponse>(`/public/bookings/${id}`, req, { params: { token } });
  return data;
}

export async function fetchRefundPreviewPublic(
  id: number, token: string,
): Promise<import('@ota/api-client').RefundQuote> {
  const { data } = await api.get<import('@ota/api-client').RefundQuote>(
    `/public/bookings/${id}/refund-preview`, { params: { token } },
  );
  return data;
}

export async function fetchModifyPreviewPublic(
  id: number, req: ModifyBookingRequest, token: string,
): Promise<import('@ota/api-client').PriceChangeQuote> {
  const { data } = await api.get<import('@ota/api-client').PriceChangeQuote>(
    `/public/bookings/${id}/modify-preview`, { params: { ...req, token } },
  );
  return data;
}

/**
 * Lists all images attached to a given target row (e.g. a room category).
 * Used by the booking detail page to render images of the room they booked.
 */
export async function fetchMediaList(
  type: 'PROPERTY' | 'ROOM_CATEGORY' | 'GUEST_UPLOAD' | 'USER_AVATAR',
  referenceId: number,
): Promise<Array<{ id: number; url: string }>> {
  const { data } = await api.get<Array<{ id: number; url: string }>>('/media/list', {
    params: { type, referenceId },
  });
  return data;
}


// ----- payments -----

/** Creates a Razorpay order for a PENDING booking. Returns orderId + keyId needed to open the checkout modal. */
export async function createPaymentOrder(bookingId: number): Promise<CreateOrderResponse> {
  const { data } = await api.post<CreateOrderResponse>(`/payments/create-order/${bookingId}`);
  return data;
}

/** Verifies the Razorpay signature on the server and confirms the booking. */
export async function verifyPayment(req: VerifyPaymentRequest): Promise<VerifyPaymentResponse> {
  const { data } = await api.post<VerifyPaymentResponse>('/payments/verify', req);
  return data;
}

/**
 * Notifies the server that the user dismissed the Razorpay modal without paying.
 * Marks the booking PAYMENT_FAILED and triggers a payment-failed email to the guest.
 * Fire-and-forget — callers should not await or depend on the result.
 */
export async function cancelPayment(bookingId: number): Promise<void> {
  await api.post(`/payments/cancel/${bookingId}`);
}

// ----- reviews -----
export async function fetchReviews(propertyId: number, page = 0, size = 10): Promise<PageResponse<ReviewResponse>> {
  const { data } = await api.get<PageResponse<ReviewResponse>>(
    `/properties/${propertyId}/reviews`,
    { params: { page, size } },
  );
  return data;
}

export async function postReview(propertyId: number, req: CreateReviewRequest): Promise<ReviewResponse> {
  const { data } = await api.post<ReviewResponse>(`/properties/${propertyId}/reviews`, req);
  return data;
}

export async function canReviewProperty(propertyId: number): Promise<boolean> {
  const { data } = await api.get<{ canReview: boolean }>(`/properties/${propertyId}/reviews/can-review`);
  return data.canReview;
}

// ----- otp auth -----
export async function sendOtp(mobile: string): Promise<void> {
  await api.post('/auth/send-otp', { mobile });
}

export async function verifyOtp(req: VerifyOtpRequest): Promise<LoginResponse> {
  const { data } = await api.post<LoginResponse>('/auth/verify-otp', req);
  authStore.set({
    expiresAt: Date.now() + data.expiresInSeconds * 1000,
    user: data.user,
  });
  return data;
}

// ----- account -----
export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  await api.post('/auth/change-password', { currentPassword, newPassword });
}

// ----- public stats -----
export interface PublicStats {
  activeProperties: number;
  citiesCovered: number;
}

export async function fetchPublicStats(): Promise<PublicStats> {
  const { data } = await api.get<PublicStats>('/properties/stats');
  return data;
}

// ----- GST slabs -----
export async function fetchGstSlabs(): Promise<GstSlabDto[]> {
  const { data } = await api.get<GstSlabDto[]>('/gst-slabs');
  return data;
}

// ----- city master -----
export interface CityItem {
  id: number;
  name: string;
  stateName: string;
  stateCode: string;
}

export async function fetchCities(): Promise<CityItem[]> {
  const { data } = await api.get<CityItem[]>('/cities');
  return data;
}
